<template>
  <div class="articles-container">
    <h2 class="h3">
      Skewness Calculator
    </h2>
    <div id="heading1" class="article-section">
      <h2 class="introduction h4">
        What Is Skewness?
      </h2>
      <p class="paragraph-introduction bottom-margins">
        Skewness is a <nuxt-link to="/tutorials/statistics-tutorials/measuring-asymmetry-skewness/" target="_blank">
          measure of probability distribution asymmetry.
        </nuxt-link> It indicates whether the observations in a dataset are concentrated on one side. This metric shows how the distribution deviates from normality and helps us comprehend the data’s fundamental structure. In fact, skewness plays a significant role in statistical modeling and hypothesis testing. If the data is skewed, the statistical test may not be accurate, and the results can be misleading.
      </p>
      <p>
        A <nuxt-link to="/tutorials/statistics-tutorials/distribution-in-statistics/" target="_blank">
          distribution
        </nuxt-link> can be right-skewed (positively skewed), left-skewed (negatively skewed), or symmetrical (zero skewness). We demonstrate how to determine the skewness type in the following sections.
      </p>
      <div class="introduction-images image-lightbox" @click="openModal('first')">
        <img
          loading="lazy" class="col-4" src="../../../../assets/images/calculators/skewness/skewness-01-desktop.png"
          alt="Positive skewness graph."
        >
        <img
          loading="lazy" class="col-4" src="../../../../assets/images/calculators/skewness/skewness-02-desktop.png"
          alt="Zero skewness graph."
        >
        <img
          loading="lazy" class="col-4" src="../../../../assets/images/calculators/skewness/skewness-03-desktop.png"
          alt="Negative skewness graph."
        >
      </div>

      <h4 class="subtitles h5">
        What Is Zero Skewness?
      </h4>
      <p class="paragraph-mean">
        Zero skew (or zero skewness) refers to a symmetrical, bell-shaped distribution where the probability of observing a value to the left or right of the mean is equal. This means that the distribution is balanced, with no long tail on either side of the mean. In other words, both sides are mirror images.
      </p>
      <img
        loading="lazy" class="zero-skewness-img" src="../../../../assets/images/calculators/skewness/skewness2-04-desktop.png"
        alt="Zero skewness graph."
      >
      <p>
        In a zero-skewed distribution, the mean, median, and mode are all equal, coinciding at the center of the distribution. This symmetry makes the zero skew easy to work with.
      </p>
      <img
        loading="lazy" class="symmetrical-distribution-img" src="../../../../assets/images/calculators/skewness/skewness-05-desktop.png"
        alt="Symmetrical distribution graph."
      >
      <p>
        Examples of distributions with zero skewness include the <nuxt-link to="/tutorials/statistics-tutorials/normal-distribution/" target="_blank">
          normal distribution
        </nuxt-link> (also known as the Gaussian distribution) and the <a href="https://mathworld.wolfram.com/UniformDistribution.html" target="_blank">
          uniform distribution
        </a>.
      </p>
      <div class="two-images-skewness">
        <img loading="lazy" src="../../../../assets/images/calculators/skewness/skewness-06-desktop.png" alt="Uniform distribution graph.">
        <img loading="lazy" src="../../../../assets/images/calculators/skewness/skewness-07-desktop.png" alt="No skew graph.">
      </div>
      <p class="bottom-margins">
        It is important to note that a zero-skewed distribution does not necessarily imply it’s a normal one. Zero skew merely indicates a balance or, in other words, an absence of skewness. A normal distribution is a distribution that fits a particular bell-shaped curve and has the following characteristics:
      </p>
      <ul>
        <li>
          It can be described by two parameters: mean and variance.
        </li>
        <li>
          Its mean and median values are equal.
        </li>
        <li>
          Approximately 68% of the data falls within one standard deviation of the mean.
        </li>
        <li>
          About 95% of the data falls within two standard deviations.
        </li>
        <li>
          Nearly 99% of the data falls within three standard deviations.
        </li>
      </ul>
      <div class="standard-deviation-images image-lightbox" @click="openModal('second')">
        <p class="standard-deviation-heading">
          Standard Deviation
        </p>
        <div>
          <img loading="lazy" src="../../../../assets/images/calculators/skewness/skewness-08-desktop.png" alt="Probability 68.3% graph.">
          <img loading="lazy" src="../../../../assets/images/calculators/skewness/skewness-09-desktop.png" alt="Probability 95.5% graph.">
          <img loading="lazy" src="../../../../assets/images/calculators/skewness/skewness-10-desktop.png" alt="Probability 99.7% graph.">
        </div>
      </div>
      <p>Men’s height is one example of a zero-skewed dataset. In general, human height and weight tend to follow a bell-shaped normal distribution that is symmetric around the mean. For example, the average height for men in the US is 5’9” (or roughly 69.2 inches). In such cases, the distribution is roughly symmetrical, with some individuals being shorter and some being taller.</p>
      <img class="male-height" src="../../../../assets/images/calculators/skewness/skewness-11-desktop.png" alt="Distribution of male height graph.">

      <h4 class="subtitles h5">
        What Is Positive Skewness?
      </h4>
      <p>Positive skew (also known as right skew) occurs when the data is not symmetrical around the mean, forming a long tail on its right side. This means that most of the distribution's observations are concentrated to the left of the peak.</p>
      <img
        loading="lazy" class="positive-skewed" src="../../../../assets/images/calculators/skewness/skewness-12-desktop.png"
        alt="Distribution skewed to the right graph."
      >
      <p>The presence of positive skewness can have several implications. In a right-skewed distribution, the mean is larger than the median, whereas the mode has the highest visual representation. That’s because the tail of the distribution pulls the mean to the right.</p>
      <img
        loading="lazy" class="positive-skewed-mmm" src="../../../../assets/images/calculators/skewness/skewness-13-desktop.png"
        alt="Positive skewed mean, median, mode graph."
      >
      <p class="bottom-margins">
        As a result, the mean is no longer a good central tendency indicator, and it cannot accurately reflect the typical value of the dataset. Performing analyses on positively skewed data violates the assumption of normality, which is typical for many statistical tests. This could affect the validity of the results and should be taken into account when interpreting statistical findings.
      </p>
      <p>The population income distribution is one example of a right skew. Typically, most people earn average volumes of income, while a few earn very high earnings. High-earners would be the outliers that skew the distribution toward the right. As a result, the distribution forms a long tail on its right side, suggesting positive skewness.</p>
      <img
        loading="lazy" class="household-incomes" src="../../../../assets/images/calculators/skewness/skewness-14-desktop.png"
        alt="Distribution of household incomes graph."
      >

      <h4 class="subtitles h5">
        What Is Negative Skewness?
      </h4>
      <p>With negative skew (also known as left skew), the left tail of the distribution extends to the peak’s left. This means that most of the observations are clustered to the right side. As a result, a negatively skewed distribution is not symmetrical around the mean, with a long tail on the left side of the peak.</p>
      <img
        loading="lazy" class="negative-skewed" src="../../../../assets/images/calculators/skewness/skewness-15-desktop.png"
        alt="Negative skewed graph."
      >
      <p class="bottom-margins">
        In a negatively skewed frequency distribution, the mean is lower than the median, whereas, once again, the mode defines the highest point. This type is also called left skew because the observations are to the left.
      </p>
      <p>
        Negative skewness can affect statistical analyses, such as hypothesis testing and regression, and the data interpretation overall. In general, the presence of a high skew makes it difficult to draw conclusions and requires some data transformation or the use of non-parametric methods. Additionally, it may signify that a dataset contains outliers on the left side or that the data is constrained by a minimum value, producing a long left tail.
      </p>
      <img
        loading="lazy" class="negative-skewed-mmm" src="../../../../assets/images/calculators/skewness/skewness-16-desktop.png"
        alt="Negative skewed mean, median, mode graph."
      >
      <p>An example of a left skew is the distribution of daily stock market returns. Investors can expect frequent small gains on most days, but the stock market could occasionally deliver huge negative returns. As a result, the distribution is likely to be skewed to the left.</p>
      <img
        loading="lazy" class="stock-market" src="../../../../assets/images/calculators/skewness/skewness-17-desktop.png"
        alt="Distribution of daily stock market returns graph."
      >
    </div>

    <div id="heading2" class="article-section">
      <h3 class="h4">
        How to Handle Skewed Data?
      </h3>
      <p>Skewed data can lead to biased estimates of central tendency (such as the mean) and affect measures of variability (such as the standard deviation). In addition, it causes incorrect inferences about the population and reduced statistical power.</p>
      <p class="bolded-text">
        There are several methods that deal with skewness in statistics:
      </p>
      <ol>
        <li>Transform the data by applying techniques such as <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4120293/" target="_blank">log transformation</a>. This method helps fit a very skewed distribution into a normal one.</li>
        <li>Remove outliers—observations that lie at an abnormal distance away from the mean. Creating histograms or box plots can help you see the distribution’s shape and identify these outliers.</li>
        <li>Use a model that does not assume a normal distribution. Non-parametric tests or generalized linear models, for example, are often more suited to analyze skewed data.</li>
      </ol>
    </div>

    <div id="heading3" class="article-section">
      <h3 class="h4">
        How to Interpret the Skewness Coefficient
      </h3>
      <p class="bottom-margins">
        You can determine the skewness of your dataset using the Skewness Calculator or the step-by-step guide above. But either way, the value is meaningless unless you know how to interpret it.
      </p>
      <p class="bottom-margins">
        The skew value varies depending on the data distribution. If a distribution is perfectly symmetrical, then the skewness is 0. On the other hand, if the tail of a distribution extends to the right, it has positive skewness. Similarly, if the tail extends to the left, the distribution has negative skewness.
      </p>
      <p class="bottom-margin-40">
        The skew’s magnitude measures the extent of asymmetry, with higher magnitudes indicating greater skewness. Although the possible values of skewness range from -3 to 3, values outside the range of -2 to 2 are not commonplace.
      </p>
      <p class="bottom-margins">
        Here is a standardly used scale to interpret skewness intensity:
      </p>
      <ul>
        <li>A value of 0 indicates a perfectly symmetrical distribution.</li>
        <li>A value between -0.5 and 0 or between 0 and 0.5 indicates an approximately symmetric distribution.</li>
        <li>A value between -1 and -0.5 or between 0.5 and 1 indicates a moderately skewed distribution.</li>
        <li>A value between -1.5 and -1 or between 1 and 1.5 indicates a highly skewed distribution.</li>
        <li>A value less than -1.5 or greater than 1.5 indicates an extremely skewed distribution.</li>
      </ul>
    </div>

    <div id="heading4" class="article-section">
      <h3 class="h4">
        What Is the Difference Between Skewness and Kurtosis?
      </h3>
      <p class="bottom-margins">
        <nuxt-link to="/tutorials/statistics-tutorials/skewness-example/" target="_blank">
          Skewness
        </nuxt-link> measures the degree of asymmetry in a distribution, with a skew of 0 indicating perfect symmetry.
      </p>
      <p class="bottom-margins">
        Kurtosis, on the other hand, measures the degree of tailedness or flatness in a distribution. This tells us what the weight of the tails is relative to the rest of the distribution. Or put simply, how much of the data is in the tails compared to the center. If a distribution has high kurtosis, it means there is more data in the tails, while low kurtosis means that it is more evenly distributed.
      </p>
      <p>
        You can obtain it using our <nuxt-link class="calculator-link" to="/calculators/kurtosis-calculator" target="_blank">
          Kurtosis Calculator
        </nuxt-link> and learn more about it in the related article.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  mounted() {
    this.renderMathJax();
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
    openModal(currentImage) {
      if (this.$store.state.layout === 'desktop') {
        this.$store.dispatch('modals/open', {
          name: 'lightBox',
          src: require(`../../../../assets/images/calculators/skewness/zoomed-${currentImage}.png`),
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.articles-container {
  .article-section {
    p {
      margin: 0;
    }

    .subtitles {
      margin-top: var(--spacing-40);
    }

    .bolded-text {
      font-weight: 500;
      font-size: 18px;
      line-height: 110%;
      margin: var(--spacing-40) 0 var(--spacing-16);
    }

    .ul-skewness {
      list-style-type: disc;
      list-style-position: outside;
      margin-top: var(--spacing-16);
      padding: 0 0 0 var(--spacing-28);

      li {
        margin: 0;
      }
    }

    .ol-skewness {
      li {
        margin: 0;
      }
    }

    .introduction-images {
      display: flex;
      margin-bottom: 0;

      img {
        margin: 0;
      }
    }

    .two-images-skewness {
      display: flex;
      justify-content: center;
      margin: var(--spacing-40) 0;

      img {
        margin: 0;
      }

      img:nth-child(1) {
        margin-right: 54px;
      }

      img:nth-child(2) {
        margin-bottom: var(--spacing-32);
      }
    }

    .standard-deviation-images {
      display: flex;
      justify-content: center;
      flex-direction: column;
      margin: var(--spacing-40) 0;

      p {
        margin-bottom: var(--spacing-22);
        align-self: center;
      }

      div {
        display: flex;
        justify-content: space-between;
      }

      img {
        margin: 0;
      }
    }

    .zero-skewness-img {
      margin-top: 0;
    }

    .standard-deviation-heading {
      font-weight: 600;
      font-size: 24px;
      line-height: 160%;
      color: var(--accent-shade-3);
    }

    .household-incomes,
    .stock-market-container,
    .male-height {
      margin-bottom: 0;
    }

    .stock-market {
      margin-bottom: 0;
    }

    .image-lightbox {
      border: unset;
      border-radius: 5px;
    }

    .introduction.h4 {
      margin-top: 0;
    }

    .paragraph-introduction {
      margin: 0;
    }

    .paragraph-mean {
      margin: 0 0 var(--spacing-40);
    }
  }
}

@include bp($bp-laptop) {
  .row .articles-container .article-section {
    .two-images-skewness {
      img:nth-child(1) {
        margin-right: 45px;
      }
    }

    .introduction-images {
      img {
        width: 217px !important;
      }
    }

    .standard-deviation-images {
      img {
        width: 206px !important;
      }
    }
  }
}

@include bp($bp-mobile) {
  .row {
    .articles-container {
      .article-section {
        .image-lightbox {
          pointer-events: none;
        }

        .subtitles {
          margin-top: var(--spacing-32);
        }

        .ul-skewness {
          padding: 0 0 0 var(--spacing-20);
        }

        .paragraph-mean,
        .zero-skewness-img {
          margin-bottom: var(--spacing-32);
        }

        .symmetrical-distribution-img,
        .positive-skewed,
        .positive-skewed-mmm,
        .negative-skewed,
        .negative-skewed-mmm {
          margin: var(--spacing-32) 0;
        }

        .male-height,
        .household-incomes,
        .stock-market-container {
          margin-top: var(--spacing-32);

          p {
            margin-bottom: var(--spacing-16);
          }
        }

        .introduction-images {
          flex-direction: column;
          margin-top: var(--spacing-32);
        }

        .two-images-skewness {
          display: flex;
          justify-content: center;
          flex-direction: column;
          margin: var(--spacing-32) 0;

          img:nth-child(1) {
            margin: 0 0 13px;
          }

          img:nth-child(2) {
            margin: 0;
          }
        }

        .standard-deviation-images {
          margin: var(--spacing-32) 0;

          &.image-lightbox img {
            width: 327px !important;
          }

          p {
            margin-bottom: var(--spacing-30);
            align-self: center;
          }

          div {
            display: flex;
            justify-content: center;
            flex-direction: column;
            gap: 24px;

            img {
              margin: 0;
            }
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .row {
    .articles-container {
      .article-section {
        .introduction-images {
          flex-direction: column;
          margin-top: var(--spacing-32);
        }

        .ol-skewness {
          padding-left: var(--spacing-20);
        }
      }
    }
  }
}

@import '../../../../assets/sass/ds/calculators/articles.scss';
</style>
